import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import type { UIMode } from '../lib/terminology';
import { userPreferencesApi } from '../api/endpoints';
import { useQueryClient } from '@tanstack/react-query';

const PREFERENCES_QUERY_KEY = ['preferences'];

interface ModeContextValue {
  mode: UIMode;
  setMode: (m: UIMode) => void;
  isBeginner: boolean;
  isLoading: boolean;
}

const ModeContext = createContext<ModeContextValue | null>(null);

export function ModeProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [mode, setModeState] = useState<UIMode>('beginner');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prefs = await userPreferencesApi.get();
        if (!cancelled && prefs?.uiMode) {
          setModeState(prefs.uiMode);
        }
      } catch {
        if (!cancelled) {
          setModeState('beginner');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setMode = useCallback(
    async (newMode: UIMode) => {
      setModeState(newMode);
      try {
        await userPreferencesApi.update(newMode);
        queryClient.setQueryData(PREFERENCES_QUERY_KEY, { uiMode: newMode });
      } catch {
        setModeState(newMode === 'beginner' ? 'advanced' : 'beginner');
      }
    },
    [queryClient]
  );

  const value: ModeContextValue = {
    mode,
    setMode,
    isBeginner: mode === 'beginner',
    isLoading,
  };

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

export function useMode(): ModeContextValue {
  const ctx = useContext(ModeContext);
  if (!ctx) {
    throw new Error('useMode must be used within ModeProvider');
  }
  return ctx;
}
